import chroma from 'chroma-js'
import { useState } from 'react'
import { HexColorPicker } from 'react-colorful'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { cn } from '~/@/lib/utils'
import '~/common/styles/color-picker.css'

interface ColorPickerProps {
  /** Current color as a hex string */
  color: string
  onChange: (hex: string) => void
  className?: string
}

/**
 * A saturation/hue picker with a hex field. Works purely in hex — callers that persist another format
 * convert on the way in and out.
 */
export const ColorPicker = ({ color, onChange, className }: ColorPickerProps) => {
  // Holds the raw text while the hex field is being edited, so partial input survives until it parses
  // as a color. Null means the field shows the current color, which is how it follows the picker.
  const [typedHex, setTypedHex] = useState<string | null>(null)

  const handleTextChange = (value: string) => {
    setTypedHex(value)
    const candidate = `#${value.replace(/^#/, '')}`
    if (chroma.valid(candidate)) {
      onChange(chroma(candidate).hex())
    }
  }

  return (
    <div className={cn('doss-color-picker flex min-w-0 flex-col gap-2', className)}>
      <HexColorPicker color={color} onChange={onChange} style={{ width: '100%', height: 160 }} />
      <ShortTextInput
        className="font-mono uppercase"
        onBlur={() => setTypedHex(null)}
        onChange={(event) => handleTextChange(event.target.value)}
        startAdornment="#"
        value={(typedHex ?? color).replace(/^#/, '')}
        // InputWrapper pads itself, which would inset the field's border from the picker above it
        wrapperClassName="p-0"
      />
    </div>
  )
}
